import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Detective, ArrowsClockwise, Gauge, Siren, Sliders, CurrencyInr, Buildings, Faders } from "@phosphor-icons/react";
import { ROUTING_CONFIG, SEVERITY_STYLE } from "@/components/fraud/FraudScoreBanner";
import { FraudRulesTable, FraudPricesTable, FraudTemplatesPanel } from "@/components/fraud/FraudConfigPanels";
import FraudTuningPanel from "@/components/fraud/FraudTuningPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, sub, testId }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md p-4" data-testid={testId}>
      <p className="label-xs">{label}</p>
      <p className="text-3xl font-black text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function FraudDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [batching, setBatching] = useState(false);
  const load = useCallback(() => axios.get(`${API}/fraud/dashboard`).then(r => setStats(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const rescanAll = async () => {
    setBatching(true);
    try {
      const r = await axios.post(`${API}/fraud/batch`, {});
      toast.success(`Queued ${r.data.queued} processed claims for fraud re-scan`);
    } catch { toast.error("Batch re-scan failed"); } finally { setBatching(false); }
  };

  const routing = stats?.routing_distribution || {};
  const total = Object.values(routing).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="fraud-dashboard-page">
      <header className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="gap-1 text-gray-600" data-testid="fraud-back-btn"><ArrowLeft size={16} /> Claims</Button>
          <div className="h-5 w-px bg-gray-200" />
          <div className="w-9 h-9 bg-red-700 rounded-md flex items-center justify-center"><Detective size={20} weight="bold" className="text-white" /></div>
          <div>
            <h1 className="text-xl tracking-tight font-semibold text-gray-900">Fraud Center</h1>
            <p className="text-xs text-gray-500">Document Fraud Detection Engine — monitoring & configuration</p>
          </div>
        </div>
        <Button onClick={rescanAll} disabled={batching} variant="outline" className="gap-2 text-sm" data-testid="fraud-batch-btn">
          <ArrowsClockwise size={16} className={batching ? "animate-spin" : ""} /> Re-scan all processed claims
        </Button>
      </header>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          <Tabs defaultValue="overview">
            <TabsList className="bg-white border border-gray-200 mb-5" data-testid="fraud-center-tabs">
              {[{ v: "overview", l: "Overview", i: Gauge }, { v: "rules", l: "Rules", i: Sliders }, { v: "tuning", l: "Threshold Tuning", i: Faders }, { v: "prices", l: "Reference Prices", i: CurrencyInr }, { v: "templates", l: "Provider Templates", i: Buildings }].map(t => {
                const I = t.i;
                return <TabsTrigger key={t.v} value={t.v} className="gap-1.5 text-sm data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid={`fc-tab-${t.v}`}><I size={15} />{t.l}</TabsTrigger>;
              })}
            </TabsList>

            <TabsContent value="overview" className="space-y-5">
              <div className="grid grid-cols-4 gap-3">
                <StatCard label="Analyzed Claims" value={stats?.analyzed_claims ?? "—"} testId="stat-analyzed" />
                <StatCard label="Average Score" value={stats?.avg_score ?? "—"} sub="composite 0-100" testId="stat-avg" />
                <StatCard label="Hard Holds" value={routing.reject_recommended || 0} sub={`${Math.round(((routing.reject_recommended || 0) / total) * 100)}% of analyzed`} testId="stat-holds" />
                <StatCard label="Investigator Feedback" value={(stats?.feedback_summary?.confirmed || 0) + (stats?.feedback_summary?.false_positive || 0)} sub={`${stats?.feedback_summary?.false_positive || 0} false positives`} testId="stat-feedback" />
              </div>

              <div className="grid grid-cols-5 gap-5">
                <div className="col-span-2 bg-white border border-gray-200 rounded-md p-4" data-testid="routing-distribution">
                  <p className="label-xs mb-3">Routing Distribution</p>
                  <div className="space-y-2">
                    {Object.entries(ROUTING_CONFIG).map(([k, c]) => (
                      <div key={k}>
                        <div className="flex justify-between text-xs mb-0.5"><span className={c.text}>{c.label}</span><span className="font-mono">{routing[k] || 0}</span></div>
                        <div className="h-2 bg-gray-100 rounded overflow-hidden"><div className={`h-full ${c.bar.replace("[&>div]:", "")}`} style={{ width: `${((routing[k] || 0) / total) * 100}%` }} /></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-span-3 bg-white border border-gray-200 rounded-md p-4" data-testid="top-alerts">
                  <p className="label-xs mb-3 flex items-center gap-1.5"><Siren size={13} /> Most Frequent Alerts</p>
                  {(stats?.top_alerts || []).length === 0 ? <p className="text-xs text-gray-400">No alerts yet.</p> : (
                    <div className="space-y-1.5">
                      {stats.top_alerts.map(a => (
                        <div key={a.alert_code} className="flex items-center gap-2 text-xs">
                          <Badge variant="outline" className={`text-[9px] w-16 justify-center ${SEVERITY_STYLE[a.severity]}`}>{a.severity}</Badge>
                          <span className="font-mono text-gray-400 w-24">{a.alert_code}</span>
                          <span className="flex-1 text-gray-800 truncate">{a.name}</span>
                          <span className="font-bold">{a.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-testid="recent-analyses">
                <div className="px-4 py-2 border-b border-gray-200 bg-gray-50"><p className="label-xs">Recent Analyses</p></div>
                <table className="w-full text-xs">
                  <tbody>
                    {(stats?.recent || []).map(r => {
                      const c = ROUTING_CONFIG[r.routing_decision] || ROUTING_CONFIG.auto_approve;
                      return (
                        <tr key={r.claim_id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/claim/${r.claim_id}?tab=fraud`)} data-testid={`recent-row-${r.claim_id}`}>
                          <td className="px-4 py-2 font-medium text-gray-900 max-w-[220px] truncate">{r.file_name}</td>
                          <td className="px-4 py-2 text-gray-600">{r.patient_name || "—"}</td>
                          <td className="px-4 py-2"><span className={`font-bold ${c.text}`}>{r.composite_score}</span></td>
                          <td className="px-4 py-2"><Badge variant="outline" className={`text-[10px] ${c.bg} ${c.text}`}>{c.label}</Badge></td>
                          <td className="px-4 py-2 text-gray-500">{r.alert_count} alerts</td>
                          <td className="px-4 py-2 text-gray-400 text-right">{r.completed_at ? new Date(r.completed_at).toLocaleString() : ""}</td>
                        </tr>
                      );
                    })}
                    {(stats?.recent || []).length === 0 && <tr><td className="px-4 py-6 text-center text-gray-400">No analyses yet — process a claim or click re-scan.</td></tr>}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="rules"><FraudRulesTable /></TabsContent>
            <TabsContent value="tuning"><FraudTuningPanel /></TabsContent>
            <TabsContent value="prices"><FraudPricesTable /></TabsContent>
            <TabsContent value="templates"><FraudTemplatesPanel /></TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
